import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Pill } from "../../components/common/Primitives";
import api, { formatApiError } from "../../lib/api";
import { formatDateTime } from "../../lib/datetime";
import { Handshake, Coins, Users, TrendingUp } from "lucide-react";

/**
 * Channel Partner portal — restricted view for external partners (CAs/CSs/
 * consultants). They see only cases attributed to their CP code, without
 * internal credit notes or ownership details.
 */
export default function PartnerDashboard() {
  const [me, setMe] = useState(null);
  const [cases, setCases] = useState(null);
  const [comms, setComms] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [m, c, k] = await Promise.all([
          api.get("/partner/me"),
          api.get("/partner/cases"),
          api.get("/partner/commissions"),
        ]);
        setMe(m.data?.partner || null);
        setCases(c.data);
        setComms(k.data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <DashboardShell title="Partner Dashboard" subtitle="Loading…"><div className="text-sm italic text-ink-muted">Loading…</div></DashboardShell>;
  }
  if (!me) {
    return <DashboardShell title="Partner Dashboard" subtitle="Access denied">
      <div className="text-sm text-rose-800">Your account isn&apos;t linked to a channel partner record. Please contact your VenturaZ channel manager.</div>
    </DashboardShell>;
  }

  return (
    <DashboardShell title={me.name} subtitle={`Channel Partner · ${me.cp_code}`}>
      <Toaster richColors position="top-right" />
      <div className="space-y-6" data-testid="partner-dashboard">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat icon={Users} label="Cases attributed" value={cases?.count ?? 0} testId="kpi-cases" />
          <Stat icon={TrendingUp} label="Requested (₹)" value={fmtINR(cases?.totals?.requested_inr)} testId="kpi-requested" />
          <Stat icon={TrendingUp} label="Disbursed (₹)" value={fmtINR(cases?.totals?.disbursed_inr)} tone="success" testId="kpi-disbursed" />
          <Stat icon={Coins} label="Commission accrued (₹)" value={fmtINR(comms?.totals?.accrued_inr)} tone="gold" testId="kpi-commission" />
        </div>

        {/* Commercials terms */}
        <div className="border border-navy/10 rounded bg-white/60 p-4" data-testid="partner-terms">
          <div className="flex items-center gap-2">
            <Handshake size={14} className="text-navy" />
            <div className="overline">Your commercial terms</div>
          </div>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <Info label="Commission % of disbursed" value={`${me.commission_pct || 0}%`} />
            <Info label="Flat commission (₹)" value={fmtINR(me.commission_flat_inr)} />
            <Info label="Products" value={(me.products || []).join(", ") || "All"} />
            <Info label="Channel manager" value={me.channel_manager_email || "—"} />
          </div>
        </div>

        {/* Cases */}
        <div className="border border-navy/10 rounded bg-white/60 p-4">
          <div className="overline mb-2">Your cases</div>
          {(!cases || cases.items.length === 0) ? (
            <div className="text-sm italic text-ink-muted">No cases yet. Refer a borrower and the VenturaZ debt desk will attribute them to your CP code.</div>
          ) : (
            <div className="border border-navy/[0.06] rounded overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-navy/[0.04] text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                  <tr>
                    <th className="text-left px-3 py-2">Case UID</th>
                    <th className="text-left px-3 py-2">Company</th>
                    <th className="text-left px-3 py-2">Amount</th>
                    <th className="text-left px-3 py-2">Stage</th>
                    <th className="text-left px-3 py-2">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-navy/[0.06]">
                  {cases.items.map((c) => (
                    <tr key={c.case_id} data-testid={`partner-case-${c.case_id}`}>
                      <td className="px-3 py-2 font-mono text-[10px]">{c.case_uid || c.case_id.slice(0, 12)}</td>
                      <td className="px-3 py-2 font-heading text-ink">{c.company_name}</td>
                      <td className="px-3 py-2">₹{fmtINR(c.amount_inr)}</td>
                      <td className="px-3 py-2"><Pill tone="gold">{c.stage}</Pill></td>
                      <td className="px-3 py-2 text-[10px] font-mono text-ink-muted">{formatDateTime(c.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Commissions ledger */}
        <div className="border border-navy/10 rounded bg-white/60 p-4">
          <div className="overline mb-2">Commissions ledger</div>
          {(!comms || comms.items.length === 0) ? (
            <div className="text-sm italic text-ink-muted">Commissions will accrue here when your referred cases are disbursed.</div>
          ) : (
            <div className="border border-navy/[0.06] rounded overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-navy/[0.04] text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                  <tr>
                    <th className="text-left px-3 py-2">Case</th>
                    <th className="text-left px-3 py-2">Case amount</th>
                    <th className="text-left px-3 py-2">Rate</th>
                    <th className="text-left px-3 py-2">Accrued (₹)</th>
                    <th className="text-left px-3 py-2">Paid (₹)</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Accrued at</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-navy/[0.06]">
                  {comms.items.map((c) => (
                    <tr key={c.commission_id} data-testid={`partner-comm-${c.commission_id}`}>
                      <td className="px-3 py-2 font-mono text-[10px]">{c.case_uid || c.case_id.slice(0, 12)}</td>
                      <td className="px-3 py-2">₹{fmtINR(c.case_amount_inr)}</td>
                      <td className="px-3 py-2">{c.commission_pct}%</td>
                      <td className="px-3 py-2 font-mono">₹{fmtINR(c.accrued_inr)}</td>
                      <td className="px-3 py-2 font-mono">₹{fmtINR(c.paid_inr)}</td>
                      <td className="px-3 py-2">
                        <Pill tone={c.status === "paid" ? "success" : c.status === "approved" ? "gold" : "muted"}>{c.status}</Pill>
                      </td>
                      <td className="px-3 py-2 text-[10px] font-mono text-ink-muted">{formatDateTime(c.accrued_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}

function Stat({ icon: Icon, label, value, tone, testId }) {
  const toneClass = tone === "success" ? "text-emerald-800" : tone === "gold" ? "text-[#8A6600]" : "text-ink";
  return (
    <div className="border border-navy/10 rounded p-3 bg-white/60" data-testid={testId}>
      <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-ink-muted">
        {Icon && <Icon size={11} />} {label}
      </div>
      <div className={`font-display text-xl mt-1 ${toneClass}`}>{value}</div>
    </div>
  );
}
function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{label}</div>
      <div className="font-heading text-sm text-ink mt-0.5 break-words">{value}</div>
    </div>
  );
}
function fmtINR(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return "0";
  return v.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}
